import type { TranscriptEntry } from '../types';

/**
 * One turn: what the test said, and what the solution said back.
 *
 * A turn is a PAIR — one test message plus the reply to it — not one message.
 * The distinction is not cosmetic: numbering every message separately made a
 * 3-turn conversation report as "7 turns total, exceeding the 3-turn
 * requirement" in the standalone tester, because the evaluator echoed the
 * inflated count straight back into its own reasoning.
 */
export interface ConversationTurn {
  /** 1-based. The opening message is not a turn and is not numbered. */
  number: number;
  userText: string;
  /** Null when the run ended before the solution answered this turn. */
  reply: TranscriptEntry | null;
}

export interface GroupedTranscript {
  /**
   * What the solution said before anyone spoke to it. Not a turn — nothing
   * was tested by it — but it is content, and reading turn 1 without it is
   * misleading.
   */
  opening: TranscriptEntry | null;
  turns: ConversationTurn[];
}

/**
 * Group a flat transcript into turns.
 *
 * The transcript is ordered, so this is a fold over it: a bot entry before
 * any user entry is the opening; every user entry starts a turn; the next bot
 * entry closes it.
 */
export const groupIntoTurns = (
  transcript: TranscriptEntry[]
): GroupedTranscript => {
  // Collected rather than folded: a greeting can arrive as several entries,
  // and merging as we go made the type of `opening` depend on its own
  // assignment.
  const openingParts: TranscriptEntry[] = [];
  const turns: ConversationTurn[] = [];

  for (const entry of transcript) {
    if (entry.role === 'user') {
      turns.push({
        number: turns.length + 1,
        userText: entry.text,
        reply: null,
      });
      continue;
    }

    const current = turns[turns.length - 1];
    if (!current) {
      // A bot entry before any user entry is part of the opening. A split
      // greeting arriving as several entries must not lose all but the
      // first.
      openingParts.push(entry);
      continue;
    }

    // Later bot entries in the same turn are merged rather than dropped. The
    // runner already merges the frames of one turn, so this only fires if a
    // transcript is assembled some other way — but dropping content silently
    // is the failure mode worth spending three lines to avoid.
    current.reply = current.reply
      ? {
          ...current.reply,
          text: `${current.reply.text}\n${entry.text}`.trim(),
          options: entry.options ?? current.reply.options,
        }
      : entry;
  }

  // Options come from the LAST part that carried any, not from the last part
  // full stop: a greeting split as "here are your choices" + a plain closing
  // line would otherwise drop the choices entirely, and the caller reads the
  // opening as one message.
  const openingOptions = openingParts.reduce<TranscriptEntry['options']>(
    (acc, part) => (part.options?.length ? part.options : acc),
    undefined
  );

  const opening =
    openingParts.length === 0
      ? null
      : {
          ...openingParts[openingParts.length - 1],
          text: openingParts
            .map((p) => p.text)
            .filter(Boolean)
            .join('\n'),
          ...(openingOptions?.length ? { options: openingOptions } : {}),
        };

  return { opening, turns };
};
